import React, { useState } from 'react';
import styled from 'styled-components';
import { 
  LeadActivityDto, 
  AddLeadNoteDto, 
  UpdateLeadNoteDto, 
  leadsApi, 
  handleApiError, 
  formatDate 
} from '../services/leadsService';

interface NotesSectionProps {
  leadId: number;
  activities: LeadActivityDto[];
  onNotesChange: (updatedActivities: LeadActivityDto[]) => void;
  currentUserId?: number;
  isSystemAdmin?: boolean;
}

const NotesContainer = styled.div`
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid var(--border-color);
`;

const NotesTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 16px;
`;

const AddNoteSection = styled.div`
  margin-bottom: 20px;
  padding: 16px;
  background: var(--background-color);
  border-radius: var(--border-radius-md);
  border: 1px solid var(--border-color);
`;

const NoteInput = styled.textarea<{ hasError?: boolean }>`
  width: 100%;
  min-height: 80px;
  padding: 10px 12px;
  border: 1px solid ${props => props.hasError ? 'var(--error-color)' : 'var(--border-color)'};
  border-radius: var(--border-radius-sm);
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  transition: border-color 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: ${props => props.hasError ? 'var(--error-color)' : 'var(--primary-color)'};
    box-shadow: 0 0 0 2px ${props => props.hasError ? 'var(--error-light)' : 'var(--primary-light)'};
  }
  
  &::placeholder {
    color: var(--text-secondary);
  }
`;

const ErrorText = styled.span`
  font-size: 12px;
  color: var(--error-color);
  margin-top: 4px;
  display: block;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 12px;
  justify-content: flex-end;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' | 'danger' }>`
  padding: 8px 16px;
  border: none;
  border-radius: var(--border-radius-sm);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  ${props => {
    switch (props.variant) {
      case 'primary':
        return `
          background: var(--primary-color);
          color: white;
          border: 1px solid var(--primary-color);
          &:hover:not(:disabled) {
            background: transparent;
            color: var(--primary-color);
          }
        `;
      case 'danger':
        return `
          background: var(--error-color);
          color: white;
          &:hover:not(:disabled) { background: #d32f2f; }
        `;
      default:
        return `
          background: var(--surface-color);
          color: var(--text-primary);
          border: 1px solid var(--border-color);
          &:hover:not(:disabled) { background: var(--hover-background); }
        `;
    }
  }}
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const NotesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const NoteItem = styled.div<{ isEditing?: boolean }>`
  padding: 12px;
  background: ${props => props.isEditing ? 'var(--primary-light)' : 'var(--surface-color)'};
  border-radius: var(--border-radius-sm);
  border: 1px solid ${props => props.isEditing ? 'var(--primary-color)' : 'var(--border-color)'};
  border-right: 3px solid var(--primary-color);
`;

const NoteMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  font-size: 12px;
  color: var(--text-secondary);
`;

const NoteActions = styled.div`
  display: flex;
  gap: 8px;
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  font-size: 12px;
  color: var(--primary-color);
  cursor: pointer;
  padding: 2px 4px;
  text-decoration: underline;
  
  &:hover {
    color: var(--primary-hover);
  }
  
  &.danger {
    color: var(--error-color);
    &:hover {
      color: #d32f2f;
    }
  }
`;

const NoteContent = styled.div`
  font-size: 14px;
  color: var(--text-primary);
  line-height: 1.4;
  white-space: pre-wrap;
`;

const EmptyState = styled.div`
  text-align: center;
  color: var(--text-secondary);
  font-size: 14px;
  padding: 20px;
  font-style: italic;
`;

const NotesSection: React.FC<NotesSectionProps> = ({
  leadId,
  activities,
  onNotesChange,
  currentUserId,
  isSystemAdmin = false
}) => {
  const [newNote, setNewNote] = useState('');
  const [editingNoteId, setEditingNoteId] = useState<number | null>(null);
  const [editingNoteContent, setEditingNoteContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filter only note activities
  const noteActivities = activities.filter(activity => activity.activityType === 6);

  const validateNote = (note: string): string | null => {
    if (!note.trim()) {
      return 'תוכן ההערה נדרש';
    }
    if (note.trim().length < 3) {
      return 'ההערה חייבת להכיל לפחות 3 תווים';
    }
    if (note.trim().length > 1000) {
      return 'ההערה לא יכולה להכיל יותר מ-1000 תווים';
    }
    return null;
  };

  const handleAddNote = async () => {
    const validationError = validateNote(newNote);
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const addNoteData: AddLeadNoteDto = { note: newNote.trim() };
      const newActivity = await leadsApi.addNote(leadId, addNoteData);
      
      // Update the activities list
      const updatedActivities = [newActivity, ...activities];
      onNotesChange(updatedActivities);
      
      setNewNote('');
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleEditNote = (noteId: number, currentContent: string) => {
    setEditingNoteId(noteId);
    setEditingNoteContent(currentContent);
    setError(null);
  };

  const handleSaveEdit = async (noteId: number) => {
    const validationError = validateNote(editingNoteContent);
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const updateNoteData: UpdateLeadNoteDto = { note: editingNoteContent.trim() };
      const updatedActivity = await leadsApi.updateNote(leadId, noteId, updateNoteData);
      
      // Update the activities list
      const updatedActivities = activities.map(activity => 
        activity.id === noteId ? updatedActivity : activity
      );
      onNotesChange(updatedActivities);
      
      setEditingNoteId(null);
      setEditingNoteContent('');
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingNoteId(null);
    setEditingNoteContent('');
    setError(null);
  };

  const handleDeleteNote = async (noteId: number) => {
    if (!window.confirm('האם אתה בטוח שברצונך למחוק הערה זו?')) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      await leadsApi.deleteNote(leadId, noteId);
      
      // Update the activities list
      const updatedActivities = activities.filter(activity => activity.id !== noteId);
      onNotesChange(updatedActivities);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const canEditNote = (note: LeadActivityDto): boolean => {
    return Boolean(isSystemAdmin || (currentUserId && note.performedByUserId === currentUserId));
  };

  return (
    <NotesContainer>
      <NotesTitle>הערות</NotesTitle>
      
      <AddNoteSection>
        <NoteInput
          value={newNote}
          onChange={(e) => setNewNote(e.target.value)}
          placeholder="הוסף הערה חדשה..."
          hasError={!!error && !editingNoteId}
          disabled={loading}
        />
        {error && !editingNoteId && <ErrorText>{error}</ErrorText>}
        
        <ButtonGroup>
          <Button onClick={() => setNewNote('')} disabled={loading || !newNote.trim()}>
            נקה
          </Button>
          <Button 
            variant="primary" 
            onClick={handleAddNote} 
            disabled={loading || !newNote.trim()}
          >
            {loading ? 'מוסיף...' : 'הוסף הערה'}
          </Button>
        </ButtonGroup>
      </AddNoteSection>

      <NotesList>
        {noteActivities.length === 0 ? (
          <EmptyState>
            אין הערות עדיין. הוסף הערה ראשונה!
          </EmptyState>
        ) : (
          noteActivities.map((note) => (
            <NoteItem key={note.id} isEditing={editingNoteId === note.id}>
              <NoteMeta>
                <span>{note.performedByUserName} • {formatDate(note.createdAt)}</span>
                {canEditNote(note) && editingNoteId !== note.id && (
                  <NoteActions>
                    <ActionButton onClick={() => handleEditNote(note.id, note.description)}>
                      ערוך
                    </ActionButton>
                    <ActionButton 
                      className="danger" 
                      onClick={() => handleDeleteNote(note.id)}
                    >
                      מחק
                    </ActionButton>
                  </NoteActions>
                )}
                {editingNoteId === note.id && (
                  <NoteActions>
                    <ActionButton onClick={() => handleSaveEdit(note.id)} disabled={loading}>
                      שמור
                    </ActionButton>
                    <ActionButton onClick={handleCancelEdit} disabled={loading}>
                      ביטול
                    </ActionButton>
                  </NoteActions>
                )}
              </NoteMeta>
              
              {editingNoteId === note.id ? (
                <>
                  <NoteInput
                    value={editingNoteContent}
                    onChange={(e) => setEditingNoteContent(e.target.value)}
                    hasError={!!error && editingNoteId === note.id}
                    disabled={loading}
                  />
                  {error && editingNoteId === note.id && <ErrorText>{error}</ErrorText>}
                </>
              ) : (
                <NoteContent>{note.description}</NoteContent>
              )}
            </NoteItem>
          ))
        )}
      </NotesList>
    </NotesContainer>
  );
};

export default NotesSection;